/**
 * Unified Theme Configuration for Backstage UI Modernization
 *
 * This module defines the modern theme configuration using Backstage's UnifiedTheme system,
 * inspired by the VW Design System (vw-design-system/colors_and_type.css).
 *
 * Design Principles:
 * - Bold Binary: White (#FFFFFF) and Dark Blue (#001E50) as primary contrast
 * - Minimal Color: Reserve color for status and actions, not decoration
 * - Card-Based Layout: White cards with 12-16px radius, subtle shadows
 * - Pill Buttons: 999px radius for primary CTAs
 * - Traffic-Light Semantics: Green (success), Yellow (caution), Red (critical)
 * - No Gradients: Flat colors only
 *
 * @see vw-design-system/colors_and_type.css for complete design tokens
 */

import { createBaseThemeOptions, createUnifiedThemeFromV4, genPageTheme, pageTheme as backstagePageTheme, palettes, shapes } from '@backstage/theme';

// VW Design System Color Palette
const vwColors = {
  // Primary
  blue600: '#001E50',      // VW Dark Blue — signature
  white: '#FFFFFF',        // VW White

  // Secondary — BlueMix (Blue600 + White tints)
  bluemix500: '#1A3562',   // White-on-Blue600 -10%
  bluemix400: '#3D5379',   // Grey50-on-Blue600 -25%
  bluemix300: '#73839E',   // White-on-Blue600 -25%
  bluemix200b: '#A3B1C6',  // White-on-Blue600 -65%

  // Grey (neutral)
  grey050: '#F2F4F7',
  grey100: '#DFE4E8',
  grey200: '#C2CACF',
  grey300: '#96A3AA',
  grey400: '#6A7A82',
  grey500: '#4C5C66',

  // DigitalCobalt (deep brand tints)
  digitalCobalt05: '#E7E8EA',
  digitalCobalt10: '#D0D3D5',
  digitalCobalt90: '#0B0E2E',
  digitalCobalt100: '#11192E',

  // Signal & Status (traffic-light semantics)
  red200: '#E4002C',       // VW Red (critical)
  red100: '#FF335C',       // VW SoftRed
  yellow200: '#F7BA00',    // VW DarkYellow
  yellow100: '#FFD100',    // VW Yellow
  green300: '#128836',     // VW ForestGreen (success)
  green150: '#48AD3E',     // VW MeadowGreen
  purple600: '#5B0B44',    // VW DeepPurple (notification)
  purple100: '#A83D7D',    // VW Purple

  // Flow Template extras
  flowBlue: '#009DE0',     // VW Brasil headers / title bars

  // Black (rare, for emphasis)
  black: '#000000',
};

// Scaffolder templates carry spec.type: "Code Template" (a display label, not one of
// Backstage's built-in type keys), so the card header's getPageTheme({ themeId: type })
// lookup misses and falls back to the default teal/green. This adds a matching entry so
// that label resolves to VW blue instead.
const pageTheme = {
  ...backstagePageTheme,
  'Code Template': genPageTheme({ colors: [vwColors.blue600], shape: shapes.wave }),
};

const tableHeaderStyles = {
  light: {
    backgroundColor: vwColors.digitalCobalt05,
    color: vwColors.blue600,
    borderColor: vwColors.digitalCobalt10,
  },
  dark: {
    backgroundColor: vwColors.digitalCobalt05,
    color: vwColors.blue600,
    borderColor: vwColors.digitalCobalt10,
  },
} as const;

// VW Design System Spacing Scale (4pt grid)
const vwSpacing = (factor: number): number => {
  const spacingMap: Record<number, number> = {
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
    16: 64,
    20: 80,
  };
  return spacingMap[factor] !== undefined ? spacingMap[factor] : factor * 8;
};

// VW Design System Border Radius Scale
const vwRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 999,
};

// VW Design System Shadow System (cool-toned, subtle elevation)
const vwShadows = [
  'none',
  '0 1px 2px rgba(0, 30, 80, 0.06), 0 1px 1px rgba(0, 30, 80, 0.04)',
  '0 2px 6px rgba(0, 30, 80, 0.08), 0 1px 2px rgba(0, 30, 80, 0.06)',
  '0 4px 12px rgba(0, 30, 80, 0.08), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 8px 20px rgba(0, 30, 80, 0.10), 0 2px 4px rgba(0, 30, 80, 0.06)',
  '0 16px 40px rgba(0, 30, 80, 0.14), 0 4px 8px rgba(0, 30, 80, 0.08)',
];

// VW Design System Motion System
const vwTransitions = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '220ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '360ms cubic-bezier(0.4, 0, 0.2, 1)',
  emphasised: '220ms cubic-bezier(0.2, 0, 0, 1)',
};

// VW Type Scale (DM Sans) — shared verbatim by both themes.
const vwTypography = {
  fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
  h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.1875, letterSpacing: '-0.01em' }, // 32px — Headline 1
  h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25 },   // 24px — Headline 2
  h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },   // 20px — Headline 3
  h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.333 },// 18px — Section Header
  h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },      // 16px
  h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.286 },// 14px
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },   // 16px — Body L
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.429 }, // 14px — Body M
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.286,
    textTransform: 'none' as const, // No uppercase transformation
    letterSpacing: '0.01em',
  },
  caption: { fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.273 }, // 11px — Caption
};

// Pill-shaped button base — identical across themes (fill/border colours differ per theme).
const sharedButtonRoot = {
  borderRadius: vwRadius.pill, // 999px for pill shape
  padding: `${vwSpacing(2)}px ${vwSpacing(6)}px`, // 8px 24px
  transition: vwTransitions.base,
  fontWeight: 600,
};

// CatalogReact picker sub-parts that are identical across themes (only `title` colour differs).
const sharedUserListPickerParts = {
  root: {
    margin: '0 !important',
    border: '0 !important',
    borderRadius: '0 !important',
    background: 'transparent !important',
    boxShadow: 'none !important',
  },
  groupWrapper: {
    margin: '0 !important',
    background: 'transparent !important',
    boxShadow: 'none !important',
    border: '1px solid var(--modern-filter-border-color)',
    borderRadius: vwRadius.md,
    overflow: 'hidden',
  },
  menuItem: {
    backgroundColor: 'transparent !important',
    borderRadius: vwRadius.md,
  },
};

// Component overrides that resolve identically in both themes — spread into each theme's
// `overrides` so the shared definition lives in exactly one place.
const sharedOverrides = {
  // CatalogFilterLayout uses <Grid item lg={2}> for the filter column and
  // <Grid item xs={12} lg={10}> for the content column (docs, catalog, templates).
  // JSS overrides are injected at runtime and win over the CSS file, so this is
  // the correct layer to fix filter-column height stretching and content-column gaps.
  MuiGrid: {
    'grid-lg-2': {
      alignSelf: 'flex-start', // prevent filter column from stretching to content height
    },
    'grid-lg-10': {
      // Only apply at lg+ where the 2-column layout is active.
      [`@media (min-width: 1280px)`]: {
        paddingLeft: `${vwSpacing(6)}px`,  // 24px gap from filter panel
        paddingRight: `${vwSpacing(6)}px`, // 24px right-edge breathing room
        boxSizing: 'border-box' as const,
      },
    },
  },
  MuiTableRow: {
    root: {
      transition: vwTransitions.fast,
      '&:hover': {
        backgroundColor: 'var(--modern-table-row-hover)',
      },
    },
  },
  // Chips - Pill-shaped with VW semantic colors
  MuiChip: {
    root: {
      borderRadius: vwRadius.pill, // 999px
      fontWeight: 600,
      fontSize: '0.75rem', // 12px
    },
  },
  // Table head row hover — transparent
  MuiTableHead: {
    root: {
      '& .MuiTableRow-root:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  // Table footer cells — no border-bottom
  MuiTableFooter: {
    root: {
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
    },
  },
  // Sidebar drawer — pill-shaped right edge, clip content
  BackstageSidebar: {
    drawer: {
      borderTopRightRadius: 24,
      borderBottomRightRadius: 24,
      overflow: 'hidden',
    },
  },
  // Page header — with transparent bg the default padding: theme.spacing(3)
  // on all four sides creates dead air. Remove bottom padding entirely so the
  // page title sits close to the content area below it.
  // Note: overrides propagate through BackstagePage's nested ThemeProvider
  // because it spreads baseTheme: (baseTheme) => ({ ...baseTheme, page: ... })
  BackstageHeader: {
    header: {
      paddingBottom: 0,
    },
  },
  // Content area — reduce paddingTop from theme.spacing(3) = 12px to 4px.
  // Together with the header paddingBottom: 0, the visual gap between the
  // page header and the first content row (action buttons) shrinks from ~24px to ~4px.
  BackstageContent: {
    root: {
      paddingTop: vwSpacing(0), // 4px
    },
  },
  // Content header — the action row sits right under the page header, with a
  // small gap before the content below it
  BackstageContentHeader: {
    container: {
      marginBottom: 'var(--modern-content-header-gap)',
    },
    rightItemsBox: {
      gap: 'var(--modern-spacing-3)',
      '& > .MuiButton-root': {
        minHeight: 44,
        padding: 'var(--modern-spacing-2) var(--modern-spacing-8)',
      },
    },
  },
  // BackstageAutocompleteBase input — subtle border matching filter token
  BackstageAutocompleteBase: {
    inputRoot: {
      '& > fieldset': {
        borderColor: 'var(--modern-control-border-color)',
        borderWidth: 'var(--modern-control-border-width)',
      },
      '&.Mui-focused > fieldset': {
        borderColor: 'var(--modern-control-border-color)',
        borderWidth: 'var(--modern-control-border-width)',
      },
    },
  },
  // BackstageSelectInputBase — subtle border matching filter token
  BackstageSelectInputBase: {
    input: {
      borderColor: 'var(--modern-control-border-color)',
      borderWidth: 'var(--modern-control-border-width)',
    },
  },
  // CatalogReact entity pickers — no margin, full width where applicable
  CatalogReactEntityKindPicker: {
    root: { margin: '0 !important' },
  },
  CatalogReactEntityTypePicker: {
    root: { margin: '0 !important' },
  },
  CatalogReactEntityOwnerPicker: {
    root: { margin: '0 !important', width: '100%' },
  },
  CatalogReactEntityAutocompletePicker: {
    root: { margin: '0 !important', width: '100%' },
  },
  CatalogReactEntityProcessingStatusPickerPicker: {
    root: { margin: '0 !important', width: '100%' },
  },
};

const createBackstageAwareThemeOptions = (options: Record<string, any>) => {
  const baseThemeOptions = createBaseThemeOptions({
    palette: options.palette,
    typography: options.typography,
    fontFamily: options.typography?.fontFamily,
    pageTheme,
  });
  const backstagePalette = options.palette?.type === 'dark' ? palettes.dark : palettes.light;

  return {
    ...baseThemeOptions,
    ...options,
    palette: {
      ...backstagePalette,
      ...options.palette,
      background: {
        ...backstagePalette.background,
        ...options.palette?.background,
      },
      text: {
        ...(backstagePalette as any).text,
        ...options.palette?.text,
      },
      primary: {
        ...backstagePalette.primary,
        ...options.palette?.primary,
      },
      secondary: {
        ...(backstagePalette as any).secondary,
        ...options.palette?.secondary,
      },
      error: {
        ...(backstagePalette as any).error,
        ...options.palette?.error,
      },
      warning: {
        ...(backstagePalette as any).warning,
        ...options.palette?.warning,
      },
      success: {
        ...(backstagePalette as any).success,
        ...options.palette?.success,
      },
      info: {
        ...(backstagePalette as any).info,
        ...options.palette?.info,
      },
      status: {
        ...backstagePalette.status,
        ...options.palette?.status,
      },
      bursts: {
        ...backstagePalette.bursts,
        ...options.palette?.bursts,
        backgroundColor: {
          ...backstagePalette.bursts?.backgroundColor,
          ...options.palette?.bursts?.backgroundColor,
        },
        gradient: {
          ...backstagePalette.bursts?.gradient,
          ...options.palette?.bursts?.gradient,
        },
      },
      banner: {
        ...backstagePalette.banner,
        ...options.palette?.banner,
      },
      navigation: {
        ...backstagePalette.navigation,
        ...options.palette?.navigation,
        navItem: {
          ...backstagePalette.navigation?.navItem,
          ...options.palette?.navigation?.navItem,
        },
        submenu: {
          ...backstagePalette.navigation?.submenu,
          ...options.palette?.navigation?.submenu,
        },
      },
      pinSidebarButton: {
        ...backstagePalette.pinSidebarButton,
        ...options.palette?.pinSidebarButton,
      },
      tabbar: {
        ...backstagePalette.tabbar,
        ...options.palette?.tabbar,
      },
    },
    typography: {
      ...baseThemeOptions.typography,
      ...options.typography,
    },
  };
};

/**
 * Light Theme Configuration (VW Primary Theme)
 * Use on White backgrounds - default theme for the application
 */
export const lightTheme = createUnifiedThemeFromV4(createBackstageAwareThemeOptions({
  palette: {
    type: 'light',

    // Primary colors - VW Dark Blue signature
    primary: {
      main: vwColors.blue600,
      light: vwColors.bluemix500,
      dark: vwColors.digitalCobalt100,
      contrastText: vwColors.white,
    },

    // Secondary colors - VW Purple for notifications/accents
    secondary: {
      main: vwColors.purple600,
      light: vwColors.purple100,
      dark: vwColors.digitalCobalt90,
      contrastText: vwColors.white,
    },

    // Background colors
    background: {
      default: vwColors.white,
      paper: vwColors.white,
    },

    // Text colors
    text: {
      primary: vwColors.blue600,
      secondary: vwColors.grey400,
    },

    // Semantic colors - VW traffic-light semantics
    error: {
      main: vwColors.red200,
      light: vwColors.red100,
      dark: vwColors.red200,
      contrastText: vwColors.white,
    },
    warning: {
      main: vwColors.yellow100,
      light: vwColors.yellow100,
      dark: vwColors.yellow200,
      contrastText: vwColors.black,
    },
    success: {
      main: vwColors.green300,
      light: vwColors.green150,
      dark: vwColors.green300,
      contrastText: vwColors.white,
    },
    info: {
      main: vwColors.flowBlue,
      light: vwColors.flowBlue,
      dark: vwColors.blue600,
      contrastText: vwColors.white,
    },

    // Divider
    divider: vwColors.grey100,

    // Navigation
    navigation: {
      background: vwColors.blue600,
      indicator: vwColors.white,
      color: vwColors.white,
      selectedColor: vwColors.white,
      navItem: {
        hoverBackground: vwColors.bluemix500,
      },
    },
  },

  // Typography - VW Type Scale using DM Sans (VW font substitute)
  typography: vwTypography,

  // Spacing - VW 4pt grid
  spacing: vwSpacing,

  // Shape - VW radius scale
  shape: {
    borderRadius: vwRadius.md, // 12px default for cards
  },

  // Shadows - VW elevation system
  shadows: vwShadows as any,

  // Component overrides - VW design patterns
  overrides: {
    ...sharedOverrides,

    // Buttons - Pill-shaped with VW Blue600 fill
    MuiButton: {
      root: sharedButtonRoot,
      contained: {
        boxShadow: vwShadows[1],
        '&:hover': {
          boxShadow: vwShadows[2],
        },
      },
      outlined: {
        borderWidth: '2px',
        '&:hover': {
          borderWidth: '2px',
        },
      },
    },

    // Cards - 12-16px radius with VW shadow
    MuiCard: {
      root: {
        borderRadius: vwRadius.md, // 12px
        boxShadow: vwShadows[1],
        backgroundColor: vwColors.white,
        transition: vwTransitions.base,
        '&:hover': {
          boxShadow: vwShadows[2],
        },
      },
    },

    // Paper - VW elevation system
    MuiPaper: {
      root: {
        borderRadius: vwRadius.md,
      },
      elevation1: {
        boxShadow: vwShadows[1],
      },
      elevation2: {
        boxShadow: vwShadows[2],
      },
      elevation3: {
        boxShadow: vwShadows[3],
      },
      elevation4: {
        boxShadow: vwShadows[4],
      },
    },

    // Text Fields - Minimal styling with VW Grey borders
    MuiTextField: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: vwRadius.sm, // 8px
          '& fieldset': {
            borderColor: vwColors.grey200,
            transition: vwTransitions.fast,
          },
          '&:hover fieldset': {
            borderColor: vwColors.grey300,
          },
          '&.Mui-focused fieldset': {
            borderColor: vwColors.blue600,
            borderWidth: '1px',
          },
        },
      },
    },

    MuiOutlinedInput: {
      root: {
        borderRadius: vwRadius.md,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: vwColors.grey200,
          transition: vwTransitions.fast,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: vwColors.grey300,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: vwColors.blue600,
          borderWidth: '1px',
        },
      },
    },

    // Tables - VW Grey borders with subtle hover
    MuiTable: {
      root: {
        borderCollapse: 'separate',
        borderSpacing: 0,
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${vwColors.grey100}`,
        padding: `${vwSpacing(3)}px ${vwSpacing(4)}px`, // 12px 16px
      },
      head: {
        fontWeight: 600,
        backgroundColor: tableHeaderStyles.light.backgroundColor,
        color: tableHeaderStyles.light.color,
        '& .MuiTableSortLabel-root, & .MuiTableSortLabel-icon, & .MuiSvgIcon-root': {
          color: 'inherit !important',
        },
      },
    },

    // Dialogs - 24px radius with VW shadow-4
    MuiDialog: {
      paper: {
        borderRadius: vwRadius.xl, // 24px
        boxShadow: vwShadows[4],
      },
    },

    // Backdrop - VW cool-toned overlay
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(0, 30, 80, 0.5)', // VW Blue600 with opacity
      },
    },

    // =========================================================
    // Backstage component overrides (Bucket B)
    // These keys are not in MUI's TS types — cast via `as any`.
    // Sub-key = the part after "ComponentName-" in the CSS class.
    // =========================================================

    // Select / Autocomplete filter labels — semibold, VW Blue text
    BackstageSelect: {
      formLabel: {
        margin: 0,
        color: vwColors.blue600,
        fontWeight: 600,
      },
    },
    BackstageAutocomplete: {
      label: {
        margin: 0,
        color: vwColors.blue600,
        fontWeight: 600,
      },
    },

    // CatalogReact picker title — block, semibold, VW Blue text
    CatalogReactUserListPicker: {
      title: {
        display: 'block',
        margin: 0,
        color: vwColors.blue600,
        fontWeight: 600,
      },
      ...sharedUserListPickerParts,
    },
  } as any,
}));

/**
 * Dark Theme Configuration (VW Secondary Theme)
 * Use on Blue600 backgrounds - for highlights, login, tutorials, promos
 */
export const darkTheme = createUnifiedThemeFromV4(createBackstageAwareThemeOptions({
  palette: {
    type: 'dark',

    // Primary colors - White on dark background
    primary: {
      main: vwColors.white,
      light: vwColors.bluemix200b,
      dark: vwColors.grey050,
      contrastText: vwColors.blue600,
    },

    // Secondary colors
    secondary: {
      main: vwColors.purple600,
      light: vwColors.purple100,
      dark: vwColors.digitalCobalt90,
      contrastText: vwColors.white,
    },

    // Background colors - VW Blue600 base
    background: {
      default: vwColors.blue600,
      paper: vwColors.bluemix500,
    },

    // Text colors
    text: {
      primary: vwColors.white,
      secondary: vwColors.bluemix200b,
    },

    // Semantic colors
    error: {
      main: vwColors.red100,
      light: vwColors.red100,
      dark: vwColors.red200,
      contrastText: vwColors.white,
    },
    warning: {
      main: vwColors.yellow100,
      light: vwColors.yellow100,
      dark: vwColors.yellow200,
      contrastText: vwColors.black,
    },
    success: {
      main: vwColors.green150,
      light: vwColors.green150,
      dark: vwColors.green300,
      contrastText: vwColors.white,
    },
    info: {
      main: vwColors.flowBlue,
      light: vwColors.flowBlue,
      dark: vwColors.bluemix300,
      contrastText: vwColors.white,
    },

    // Divider
    divider: vwColors.bluemix400,

    // Navigation
    navigation: {
      background: vwColors.digitalCobalt100,
      indicator: vwColors.white,
      color: vwColors.bluemix200b,
      selectedColor: vwColors.white,
      navItem: {
        hoverBackground: vwColors.bluemix500,
      },
    },
  },

  // Typography - Same as light theme
  typography: vwTypography,

  // Spacing - VW 4pt grid
  spacing: vwSpacing,

  // Shape - VW radius scale
  shape: {
    borderRadius: vwRadius.md,
  },

  // Shadows - Dark theme shadows (layering on Blue600)
  shadows: [
    'none',
    '0 2px 6px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.25)',
    '0 2px 6px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.25)',
    '0 4px 12px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
    '0 16px 40px rgba(0, 0, 0, 0.50), 0 4px 8px rgba(0, 0, 0, 0.35)',
  ] as any,

  // Component overrides - Same structure as light theme
  overrides: {
    ...sharedOverrides,

    MuiButton: {
      root: sharedButtonRoot,
      contained: {
        backgroundColor: vwColors.white,
        color: vwColors.blue600,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.25)',
        '&:hover': {
          backgroundColor: vwColors.grey050,
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
        },
      },
      outlined: {
        borderWidth: '2px',
        borderColor: vwColors.white,
        color: vwColors.white,
        '&:hover': {
          borderWidth: '2px',
          backgroundColor: vwColors.bluemix500,
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: vwRadius.md,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.25)',
        transition: vwTransitions.base,
        backgroundColor: vwColors.digitalCobalt100,
        '&:hover': {
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.40), 0 2px 4px rgba(0, 0, 0, 0.30)',
        },
      },
    },
    MuiPaper: {
      root: {
        borderRadius: vwRadius.md,
        backgroundColor: vwColors.digitalCobalt100,
      },
    },
    MuiTextField: {
      root: {
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: vwRadius.sm,
          '& fieldset': {
            borderColor: vwColors.bluemix300,
            transition: vwTransitions.fast,
          },
          '&:hover fieldset': {
            borderColor: vwColors.bluemix200b,
          },
          '&.Mui-focused fieldset': {
            borderColor: vwColors.white,
            borderWidth: '1px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: vwRadius.md,
        '& .MuiOutlinedInput-notchedOutline': {
          // Match --modern-control-border-color in dark mode: subtle rgba border
          // (was vwColors.bluemix300 = #73839E — too visible compared to other controls)
          borderColor: 'rgba(163, 177, 198, 0.12)',
          transition: vwTransitions.fast,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(163, 177, 198, 0.28)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: vwColors.white,
          borderWidth: '1px',
        },
      },
    },
    MuiInputBase: {
      root: {
        color: vwColors.white,
      },
      input: {
        color: vwColors.white,
        '&::placeholder': {
          color: 'rgba(255, 255, 255, 0.64)',
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${vwColors.bluemix400}`,
        padding: `${vwSpacing(3)}px ${vwSpacing(4)}px`,
      },
      head: {
        fontWeight: 600,
        backgroundColor: tableHeaderStyles.dark.backgroundColor,
        color: tableHeaderStyles.dark.color,
        '& .MuiTableSortLabel-root, & .MuiTableSortLabel-icon, & .MuiSvgIcon-root': {
          color: 'inherit !important',
        },
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: vwRadius.xl,
        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.50), 0 4px 8px rgba(0, 0, 0, 0.35)',
        backgroundColor: vwColors.digitalCobalt100,
        backgroundImage: 'none',
        border: `1px solid ${vwColors.bluemix400}`,
      },
    },
    MuiDialogTitle: {
      root: {
        backgroundColor: vwColors.digitalCobalt100,
        color: vwColors.white,
      },
    },
    MuiDialogContent: {
      root: {
        backgroundColor: vwColors.digitalCobalt100,
        color: vwColors.white,
      },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: vwColors.digitalCobalt100,
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.76)',
      },
    },

    // =========================================================
    // Backstage component overrides (Bucket B)
    // These keys are not in MUI's TS types — cast via `as any`.
    // Sub-key = the part after "ComponentName-" in the CSS class.
    // =========================================================

    // Select / Autocomplete filter labels — semibold, white text on dark bg
    BackstageSelect: {
      formLabel: {
        margin: 0,
        color: vwColors.white,
        fontWeight: 600,
      },
    },
    BackstageAutocomplete: {
      label: {
        margin: 0,
        color: vwColors.white,
        fontWeight: 600,
      },
    },

    // EmptyState — center text + illustration, fix image from side-by-side positioning.
    // Dark mode additionally strips the #001E50 background and fades the white SVG.
    BackstageEmptyState: {
      root: {
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center' as any,
        padding: '32px 16px 16px',
        backgroundColor: 'transparent',
      },
    },
    BackstageEmptyStateImage: {
      generalImg: {
        // Reset the relative + left:50% + translate centering trick (only works side-by-side).
        position: 'static',
        transform: 'none',
        left: 'auto',
        top: 'auto',
        width: '800px',
        maxWidth: '100%',
        margin: '8px auto 0',
        display: 'block',
        // Dark: white SVG on dark bg is jarring — fade it out significantly.
        opacity: 0.08,
      },
    },

    // CatalogReact picker title — block, semibold, white text on dark bg
    CatalogReactUserListPicker: {
      title: {
        display: 'block',
        margin: 0,
        color: vwColors.white,
        fontWeight: 600,
      },
      ...sharedUserListPickerParts,
    },
  } as any,
}));

// Export default theme (light)
export default lightTheme;
